import { ConnectionAreaUsageVO, ConnectionVO, MetaElementUsageIdentityVO } from '../vo/meta-element.vo';
import { ArrayUtils } from './array-utils';

export class ConnectionAreaUsageUtils {

  public static isForwardConnection(connectionAreaUsage: ConnectionAreaUsageVO): boolean {
    return connectionAreaUsage.identity.equals(connectionAreaUsage.connection.acceptingAreaId) &&
      !connectionAreaUsage.connection.givingAreaIds.some(identity => identity.equals(connectionAreaUsage.identity));
  }

  public static isBackwardConnection(connectionAreaUsage: ConnectionAreaUsageVO): boolean {
    return !connectionAreaUsage.identity.equals(connectionAreaUsage.connection.acceptingAreaId) &&
      connectionAreaUsage.connection.givingAreaIds.some(identity => identity.equals(connectionAreaUsage.identity));
  }

  public static connectedMetaElementUsageIdentities(connectionAreaUsage: ConnectionAreaUsageVO): MetaElementUsageIdentityVO[] {
    if(this.isForwardConnection(connectionAreaUsage)) {
      return connectionAreaUsage.connection.givingAreaIds;
    } else if (this.isBackwardConnection(connectionAreaUsage)) {
      return [connectionAreaUsage.connection.acceptingAreaId];
    } else {
      return [];
    }
  }

  public static backwardConnectedArticleUsageId(connectionAreaUsage: ConnectionAreaUsageVO): string | undefined {
    return this.isBackwardConnection(connectionAreaUsage)
      ? connectionAreaUsage.connection.acceptingAreaId.articleUsageId
      : undefined;
  }

  public static backwardConnectedArticleUsageIds(connectionAreaUsage: ConnectionAreaUsageVO): string[] {
    const metaElementUsageIdentities = this.backwardConnectedMetaElementUsageIdentities(connectionAreaUsage);
    const articleUsageIds = metaElementUsageIdentities.map(this.articleUsageId);
    return [...new Set(articleUsageIds)];
  }

  public static backwardConnectedMetaElementUsageIdentities(connectionAreaUsage: ConnectionAreaUsageVO): MetaElementUsageIdentityVO[] {
    if(this.isBackwardConnection(connectionAreaUsage)) {
      return this.connectedMetaElementUsageIdentities(connectionAreaUsage);
    }
    return [];
  }

  public static forwardConnectedArticleUsageIds(connectionAreaUsage: ConnectionAreaUsageVO): string[] {
    const metaElementUsageIdentities = this.forwardConnectedMetaElementUsageIdentities(connectionAreaUsage);
    const articleUsageIds = metaElementUsageIdentities.map(this.articleUsageId);
    return [...new Set(articleUsageIds)];
  }

  public static forwardConnectedMetaElementUsageIdentities(connectionAreaUsage: ConnectionAreaUsageVO): MetaElementUsageIdentityVO[] {
    if(this.isForwardConnection(connectionAreaUsage)) {
      return this.connectedMetaElementUsageIdentities(connectionAreaUsage);
    }
    return [];
  }

  private static articleUsageId(metaElementUsageIdentity: MetaElementUsageIdentityVO): string {
    return metaElementUsageIdentity.articleUsageId;
  }

  public static isFreeConnectionUsage(connectionAreaUsage: ConnectionAreaUsageVO, multiplicity: number = 0): boolean {
    let isFree = true;
    if (this.isForwardConnection(connectionAreaUsage)) {
      if (multiplicity < 0) {
        isFree = false;
      } else if (multiplicity > 0) {
        let distinctGivingArticleUsageIds =
          ArrayUtils.distinct(connectionAreaUsage.connection.givingAreaIds.map(id => id.articleUsageId));
        if(multiplicity <= distinctGivingArticleUsageIds.length) {
          isFree = false;
        }
      }
    } else {
      // if giving usage
      isFree = false;
    }

    return isFree;
  }

  public static hasGivingAreaIdentities(connectionAreaUsage: ConnectionAreaUsageVO) {
    return connectionAreaUsage.connection.givingAreaIds.length > 0;
  }

  public static connect(acceptingUsage: ConnectionAreaUsageVO, givingUsage: ConnectionAreaUsageVO) {
    let connection: ConnectionVO = acceptingUsage.connection;
    if (!connection) {
      connection = new ConnectionVO();
      connection.acceptingAreaId = acceptingUsage.identity;
      connection.givingAreaIds = [];
    }
    connection.givingAreaIds.push(givingUsage.identity);

    acceptingUsage.connection = connection;
    givingUsage.connection = connection;
  }

  public static setBackwardConnection(connectionAreaUsage: ConnectionAreaUsageVO, acceptingAreaId: MetaElementUsageIdentityVO) {
    const connection: ConnectionVO = new ConnectionVO();
    connection.acceptingAreaId = acceptingAreaId;
    connection.givingAreaIds = [connectionAreaUsage.identity];
    connectionAreaUsage.connection = connection;
  }

  public static metaElementIdentities(connectionAreaUsage: ConnectionAreaUsageVO): MetaElementUsageIdentityVO[] {
    return [connectionAreaUsage.identity, connectionAreaUsage.connection.acceptingAreaId, ...connectionAreaUsage.connection.givingAreaIds];
  }
}
